/**
 * №11.1.3 / №13.1 (§1.7) — bounded aggregate read for the chrome. Related-list
 * headers ("14 posted · $118,240 total") are computed live server-side via
 * `aggregate_entities` (COUNT / SUM over a filter, no 50-row cap) — never a stored
 * roll-up (G2). Thin wrapper: a `{count, sum, filter}` request maps to the engine's
 * {filters, values, count} shape and back to a flat `{count, sum}`.
 */
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

export interface AggregateSpec {
  count?: boolean;
  /** SUM(field) — returned as `sum`. */
  sum?: string;
  /** Equality filter (field → value). */
  filter?: Record<string, string>;
}

interface AggregateResult { count?: number; sum?: number }

export function useAggregate(typeSlug: string, spec: AggregateSpec) {
  return useQuery({
    queryKey: ["aggregate", typeSlug, spec],
    staleTime: 10_000,
    queryFn: async (): Promise<AggregateResult> => {
      const filters = Object.entries(spec.filter ?? {}).map(([field, value]) => ({ field, op: "eq", value }));
      const values = spec.sum ? [{ as: "sum", field: spec.sum }] : [];
      const r = await mcpClient.toolsCall<{ count?: number; totals?: Record<string, number> }>("aggregate_entities", {
        type_slug: typeSlug,
        filters,
        values,
        count: spec.count ?? true,
      });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "aggregate_entities failed");
      const data = r.structuredContent ?? {};
      // Summed values come back under `totals` keyed by the requested `as` name.
      return { count: data.count, sum: data.totals?.sum };
    },
  });
}
